<template>
  <div>
    <data-table
      :config="datatableConfig"
      :data="chamados"
      :refreshButton="refreshButtonConfig"
      @row-click="handleRowClick"
      @refresh="handleRefresh">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import DataTable from '../../grid/DataTable.vue';
import { log } from '../../utils/DebugLogger';
import { textoPuro } from '../../utils/textoPuro';
import { useDateFormatter } from '../../composables/useDateFormatter';
import { STATUS_MEANING, ticketExplanations } from './explanations';
import type { Chamados_ChamadoRow } from '../../contracts/Chamados_ChamadoRow';

/**
 * ChamadosTable — a lista de chamados.
 *
 * Recebe as linhas por PROP (`data`), em vez de buscar de um `endpoint` como as
 * tabelas de CRUD do sistema: a resposta de `/chamados/lista` traz `sou_suporte`
 * JUNTO das linhas, e quem guarda esse estado é o composable. Se a tabela
 * buscasse sozinha, ou a página faria uma segunda chamada só para saber quem é
 * o usuário, ou os dois estados poderiam ficar de épocas diferentes.
 *
 * A tabela é de LEITURA: não tem editar nem excluir. Chamado não se apaga —
 * cancela-se, e isso é transição de status, feita no detalhe. Uma lixeira aqui
 * ofereceria uma ação que o backend não tem.
 */

interface CellParams {
  data: Chamados_ChamadoRow;
  value?: unknown;
}

const props = defineProps<{
  chamados: Chamados_ChamadoRow[];
  /** Vem do backend; a tabela nunca deriva isso dos grupos. */
  souSuporte: boolean;
}>();

const emit = defineEmits<{
  abrir: [id: number];
  atualizar: [];
}>();

const { t } = useI18n();

const EX = computed(() => ticketExplanations(t));
const { fromApiDateTimeFormat, formatDateTime } = useDateFormatter();

/**
 * ISO 8601 UTC (o que a API manda) -> dd/mm/aaaa hh:mm:ss (o que o humano le).
 *
 * Passa pelo composable de propósito, em vez de fatiar a string: ele é a
 * FRONTEIRA DE CONVERSÃO da plataforma (skill `timezone-handling`), e é ele
 * que sabe que o banco guarda GMT-3 e a API fala UTC. Formatar na mão aqui
 * daria 3 horas de erro sem nenhum sintoma além de um horário estranho.
 */
const dataHora = (iso: unknown): string => {
  if (typeof iso !== 'string' || iso === '') return '—';

  const d = fromApiDateTimeFormat(iso);

  return d ? formatDateTime(d) : '—';
};

/**
 * Rótulo legível de cada status. O mapa é de APRESENTAÇÃO — a regra de quais
 * status existem e para onde cada um pode ir vive no backend
 * (`ChamadoStatus`), e chega pronta em `transicoes_possiveis`.
 */
const ROTULO_STATUS: Record<string, string> = {
  aberto: 'Aberto',
  em_atendimento: 'Em atendimento',
  aguardando_solicitante: 'Aguardando você',
  resolvido: 'Resolvido',
  fechado: 'Fechado',
  cancelado: 'Cancelado',
};

const ROTULO_PRIORIDADE: Record<string, string> = {
  baixa: 'Baixa',
  normal: 'Normal',
  alta: 'Alta',
  critica: 'Crítica',
};

/**
 * Severidade do Badge (componente da casa, `renderType: 'badge'` do
 * DataTable) por status — a MESMA tarja usada em ~15 outras tabelas do
 * sistema, e não uma cor inventada para chamados.
 */
const SEVERIDADE_STATUS: Record<string, string> = {
  aberto: 'info',
  em_atendimento: 'warn',
  aguardando_solicitante: 'secondary',
  resolvido: 'success',
  fechado: 'contrast',
  cancelado: 'danger',
};

/** Severidade do Badge por prioridade — a cor É o ícone: sem depender de
 * vermelho vs. laranja para quem não os distingue, a ESCALA (secondary ->
 * info -> warn -> danger) já ordena a leitura visualmente. */
const SEVERIDADE_PRIORIDADE: Record<string, string> = {
  baixa: 'secondary',
  normal: 'info',
  alta: 'warn',
  critica: 'danger',
};

/** O que cada esforço estimado quer dizer — a escala de ChamadoEsforco no app-api. */
const DESCRICAO_ESFORCO: Record<string, string> = {
  S: 'Até 3 horas',
  M: 'Meio dia a um dia',
  L: 'Dois a cinco dias',
  XL: 'Mais de uma semana — dividir',
};

const tableColumns = computed((): Record<string, unknown>[] => {
  return [
    {
      data: 'codigo',
      title: t('Código'),
      width: 160,
      sortable: true,
      filterType: 'text',
      // `custom_clickable_id`, e NAO `clickable_id`: o `clickable_id` emite so o
      // VALOR da celula (o codigo "2026-0007"), e o handler precisa do id
      // numerico da linha. O callback recebe a linha inteira.
      renderType: 'custom_clickable_id',
      clickCallback: (_valor: unknown, linha: Chamados_ChamadoRow) => handleRowClick(linha),
      // Prioridade (e Franquia, para quem enxerga mais de uma) viram
      // sub-linhas do código via `mergedColumns` — o recurso da própria
      // DataTable para "esta segunda informação pertence ao lado da
      // primeira", já usado em ~5 outros domínios (SOStockTable,
      // BillingThreeSixtyTable...). `renderType: 'badge'` é o pill que a casa
      // já usa para severidade em toda parte — não uma tarja de CSS inventada.
      mergedColumns: [
        {
          data: 'prioridade',
          title: t('Prioridade'),
          renderType: 'badge',
          badgeConfig: (row: Record<string, unknown>) => {
            const prioridade = String(row.prioridade ?? '');

            return {
              label: t(ROTULO_PRIORIDADE[prioridade] ?? prioridade),
              severity: SEVERIDADE_PRIORIDADE[prioridade] ?? 'secondary',
            };
          },
        },
        // NOME, nunca o id — mesma regra da antiga coluna própria de
        // Franquia. Só existe para quem enxerga mais de uma franquia; para o
        // solicitante seria a mesma linha repetida em toda a tabela.
        ...(props.souSuporte
          ? [{ data: 'franquia_nome', title: t('Franquia') }]
          : []),
        // O ESFORÇO que o agente estimou ao planejar (S, M, L, XL —
        // docs/plans/steward-auto-plan.md). Só o suporte o recebe; chamado
        // ainda não planejado não ganha linha vazia.
        ...(props.souSuporte
          ? [
              {
                data: 'agente_esforco',
                title: t('Esforço'),
                cellRenderer: (params: CellParams) => {
                  const span = document.createElement('span');
                  const esforco = params.data.agente_esforco;
                  if (esforco) {
                    span.textContent = `${t('Esforço')}: ${esforco}`;
                    span.title = t(DESCRICAO_ESFORCO[esforco] ?? esforco);
                  }

                  return span;
                },
              },
            ]
          : []),
      ],
    },
    // QUEM abriu, pelo nome gravado na abertura. Só o suporte precisa: o
    // solicitante só vê os próprios chamados. Sem nome no JWT, mostra "—".
    ...(props.souSuporte
      ? [
          {
            data: 'aberto_por_nome',
            title: t('Aberto por'),
            width: 180,
            sortable: true,
            filterType: 'text',
            cellRenderer: (params: CellParams) => {
              const span = document.createElement('span');
              span.textContent = String(params.data.aberto_por_nome ?? '').trim() || '—';

              return span;
            },
          },
        ]
      : []),
    {
      data: 'status',
      title: t('Status'),
      width: 170,
      sortable: true,
      renderType: 'badge',
      explanation: EX.value.statusColumn,
      cellExplanation: (row: Record<string, unknown>) => {
        const meaning = STATUS_MEANING[String(row.status ?? '')];

        return meaning ? { icon: meaning.icon, what: t(meaning.text) } : null;
      },
      badgeConfig: (row: Record<string, unknown>) => {
        const status = String(row.status ?? '');

        return {
          label: t(ROTULO_STATUS[status] ?? status),
          severity: SEVERIDADE_STATUS[status] ?? 'secondary',
        };
      },
    },
    {
      data: 'assunto',
      title: t('Assunto'),
      sortable: true,
      filterType: 'text',
      flex: 1,
    },
    {
      // A DATA da mensagem mais recente do fio — não `updated_at` (que muda
      // com qualquer edição administrativa). É o que responde "quem está
      // esperando há mais tempo?"; sem mensagem no fio ainda, mostra "—".
      data: 'ultima_interacao_em',
      title: t('Última interação'),
      width: 220,
      sortable: true,
      cellRenderer: (params: CellParams) => {
        const span = document.createElement('span');
        span.textContent = dataHora(params.data.ultima_interacao_em);

        return span;
      },
      // O TEXTO dessa mesma mensagem, como sub-linha — a data sozinha diz
      // "quando"; a coluna existe para responder também "o quê". Texto PURO,
      // sem o HTML do rich text (ver `textoPuro`).
      mergedColumns: [
        {
          data: 'ultima_interacao_texto',
          title: t('Mensagem'),
          cellRenderer: (params: CellParams) => textoPuro(params.data.ultima_interacao_texto),
        },
      ],
    },
    {
      // A API devolve ISO 8601 UTC (o Typed Data Layer da plataforma); aqui
      // vira dd/mm/aaaa hh:mm:ss no fuso de quem le. `renderType: 'datetime'`
      // sozinho nao fazia a conversao — mostrava o texto cru.
      data: 'created_at',
      title: t('Aberto em'),
      width: 170,
      sortable: true,
      cellRenderer: (params: CellParams) => {
        const span = document.createElement('span');
        span.textContent = dataHora(params.data.created_at);

        return span;
      },
    },
  ];
});

const datatableConfig = computed(() => ({
  filename: `chamados_${new Date().toISOString().split('T')[0]}`,
  columns: tableColumns.value,
  pageLength: 25,
  pagination: true,
  rowSelection: 'single',
  sortable: true,
  resizableColumns: true,
  showFilters: true,
  filterDisplay: 'row',
  stripedRows: true,
  showGridlines: true,
  size: 'small',
}));

const refreshButtonConfig = computed(() => ({
  show: true,
  text: t('Atualizar'),
}));

const handleRowClick = (row: Chamados_ChamadoRow): void => {
  if (!row || typeof row.id !== 'number') return;

  log('ChamadosTable', 'handleRowClick', 'Abrindo chamado', row.id);
  emit('abrir', row.id);
};

const handleRefresh = (): void => {
  emit('atualizar');
};
</script>
